import "./init";
import { StrictMode, Suspense, useMemo } from "react";

import { ApolloProvider } from "@apollo/client";
import { EmbedThemeProvider } from "@hl/base-components/lib/theme/provider";
import { AuthProvider } from "@hl/shared-features/lib/features/auth";
import { EmbedAppBootstrap } from "@hl/shared-features/lib/features/auth/AppBootstrap";
import TransactionProvider from "@hl/shared-features/lib/features/evm-tx/TransactionProvider";
import { EmbedTheme } from "@hl/shared-features/lib/features/mint/types";
import { ModalStack } from "@hl/shared-features/lib/features/modal";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { debounce, pick } from "lodash";
import { createRoot } from "react-dom/client";

import {
  MintCardEmbed,
  EMBED_CARD_STYLE_NAMES,
} from "./features/MintPage/EmbedCard";
import {
  EmbedFlags,
  EmbedFlagsContext,
  embedFlagsFromAttributes,
} from "./features/MintPage/embed/customize";
import { generateMantineTheme } from "./features/MintPage/embed/theme";
import MintPage from "./features/MintPage/index";
import Root, { MintCardEmbedModals } from "./features/layout/Root";
import { createEmbedApolloClient } from "./services/apolloClient";

import "./app.css";

try {
  TimeAgo.addDefaultLocale(en);
} catch (err) {
  console.warn("time ago init error", err);
  // ignore error, as site can have TimeAgo initialized already
}

type EmbedAppProps = {
  collectionId: string;
  customerEmbedId: string;
  flags: EmbedFlags;
};
const EmbedApp: React.FC<EmbedAppProps> = ({ collectionId, flags }) => {
  // TODO: https://linear.app/highlight-xyz/issue/ENG-3667/analytics-for-mint-embeds

  return (
    <Suspense fallback={""}>
      <EmbedFlagsContext.Provider value={flags}>
        <EmbedThemeProvider enableDarkMode={flags.enableDarkMode}>
          <AuthProvider>
            <EmbedAppBootstrap>
              <TransactionProvider>
                <ModalStack>
                  <Root>
                    <MintPage embedCollectionId={collectionId} isEmbed />
                  </Root>
                </ModalStack>
              </TransactionProvider>
            </EmbedAppBootstrap>
          </AuthProvider>
        </EmbedThemeProvider>
      </EmbedFlagsContext.Provider>
    </Suspense>
  );
};

type EmbedCardProps = EmbedAppProps & {
  classNames?: Record<string, unknown>;
  styles?: Record<string, unknown>;
  theme?: Record<string, unknown>;
  containerHtml?: string;
};

const EmbedCard: React.FC<EmbedCardProps> = ({
  collectionId,
  flags,
  theme,
  ...other
}) => {
  const customTheme = useMemo(() => {
    if (!theme) {
      return;
    }
    return generateMantineTheme(theme as EmbedTheme);
  }, [theme]);

  return (
    <Suspense fallback={""}>
      <EmbedFlagsContext.Provider value={flags}>
        <EmbedThemeProvider customTheme={customTheme}>
          <AuthProvider>
            <EmbedAppBootstrap>
              <TransactionProvider>
                <ModalStack>
                  <MintCardEmbed collectionId={collectionId} {...other} />
                  <MintCardEmbedModals collectionId={collectionId} />
                </ModalStack>
              </TransactionProvider>
            </EmbedAppBootstrap>
          </AuthProvider>
        </EmbedThemeProvider>
      </EmbedFlagsContext.Provider>
    </Suspense>
  );
};

const renderPage = (container: Element, props: EmbedAppProps) => {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ApolloProvider client={createEmbedApolloClient(props.customerEmbedId)}>
        <EmbedApp {...props} />
      </ApolloProvider>
    </StrictMode>
  );
};

const renderCard = (container: Element, props: EmbedCardProps) => {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ApolloProvider client={createEmbedApolloClient(props.customerEmbedId)}>
        <EmbedCard {...props} containerHtml={container.innerHTML} />
      </ApolloProvider>
    </StrictMode>
  );
};

let stopRender = false;

const stringToObj = (input: string, attrName: string) => {
  if (!input) {
    return {};
  }
  const text = input
    // replace single quotes
    .replace(/'/g, '"')
    // ensure proper double quotes around property names
    .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');
  let json = {};
  try {
    json = JSON.parse(text);
  } catch (err) {
    console.error(`failed to parse JSON string in ${attrName}`, { err });
  }
  return json;
};

const getEmbedProps = (
  container: Element,
  embedType: "card" | "page"
): EmbedAppProps | EmbedCardProps => {
  const readObjAttribute = (attrName: string, allowedProps?: string[]) => {
    const attrVal = container.getAttribute(attrName);
    let data;
    if (typeof attrVal === "string") {
      data = stringToObj(attrVal, attrName);
    } else if (typeof attrVal === "object") {
      data = attrVal;
    }
    if (data && allowedProps) {
      return pick(data, allowedProps);
    }
    return data;
  };

  return {
    collectionId: container.getAttribute("data-mint-collection-id") ?? "",
    customerEmbedId: container.getAttribute("data-customer-embed-id") ?? "beta",
    flags: embedFlagsFromAttributes(container) as EmbedFlags,
    ...(embedType === "card" && {
      styles: readObjAttribute("data-styles", EMBED_CARD_STYLE_NAMES),
      classNames: readObjAttribute("data-class-names", EMBED_CARD_STYLE_NAMES),
      theme: readObjAttribute("data-theme"),
    }),
  };
};

document
  .querySelectorAll(`[data-widget="highlight-mint-page"]`)
  .forEach((container) => {
    const props = getEmbedProps(container, "page");
    if (!stopRender && props.collectionId) {
      renderPage(container, props);
      // multiple HL pages instances are not supported
      stopRender = true;
    }
  });

const loadCards = () => {
  if (stopRender) {
    return;
  }

  document
    .querySelectorAll(`[data-widget="highlight-mint-card"]`)
    .forEach((container) => {
      const props = getEmbedProps(container, "card");
      const { collectionId } = props;
      if (collectionId) {
        if (container.getAttribute("data-rendered") === "true") {
          // already rendered, ignore
          return;
        }

        const placeholders = document.querySelectorAll(
          `[data-mint-collection-id="${collectionId}"]`
        );
        if (placeholders.length > 1 && placeholders[0] !== container) {
          // ignore all other placeholders with the same id
          // dynamically checking html to support live html editors
          // where embed code can be removed and added without page reload
          return;
        }
        renderCard(container, props);
        container.setAttribute("data-rendered", "true");
      }
    });
};

loadCards();

if (window.MutationObserver) {
  const loadCardsDebounced = debounce(loadCards, 100);

  const callback: MutationCallback = (mutationList) => {
    const isAdded = mutationList.find((mutation) => mutation.addedNodes.length);
    if (isAdded) {
      loadCardsDebounced();
    }
  };
  // watch for changes in document structure
  const observer = new MutationObserver(callback);
  const body = document.querySelector("body");
  if (body) {
    observer.observe(body, {
      attributes: false,
      childList: true,
      subtree: true,
    });
  }
}
